import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { resumeAudioContext } from '../lib/audioEngine'
import { subscribeToPushNotifications, unsubscribeAllPushNotifications } from '../lib/pushSubscription'
import { canInstallPWA, promptPWAInstall, subscribePWAInstall } from '../lib/pwaInstall'
import { translateStatic, useI18n } from '../i18n/useI18n'
import type { GameSettings, LanguageCode } from '../game/types'
import { useGameStore } from '../store/useGameStore'

type Props = {
  userEmail: string | null | undefined
  onBack: () => void
  onSignOut: () => void | Promise<void>
}

export function AccountScreen({ userEmail, onBack, onSignOut }: Props) {
  const { t, formatDateTime } = useI18n()
  const settings = useGameStore((s) => s.settings)
  const updateSettings = useGameStore((s) => s.updateSettings)
  const lastSyncAt = useGameStore((s) => s.lastSyncAt)
  const isSyncing = useGameStore((s) => s.isSyncing)
  const syncError = useGameStore((s) => s.syncError)
  const flushSync = useGameStore((s) => s.flushSync)
  const syncFromCloud = useGameStore((s) => s.syncFromCloud)
  const resetProgressToNewGame = useGameStore((s) => s.resetProgressToNewGame)

  const [resetConfirmStep, setResetConfirmStep] = useState<0 | 1>(0)
  const [pushBusy, setPushBusy] = useState(false)
  const [canInstall, setCanInstall] = useState(canInstallPWA)

  useEffect(() => subscribePWAInstall(() => setCanInstall(canInstallPWA())), [])

  const setPartial = (partial: Partial<GameSettings>) => {
    updateSettings(partial)
  }

  return (
    <div className="space-y-6 pb-8">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onBack}
          className="rounded-lg border border-stone-700 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-stone-200 transition hover:border-amber-600 hover:text-amber-300"
        >
          {t('account_back')}
        </button>
      </div>

      <Section title={t('account_header_account')}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <span className="text-[11px] uppercase tracking-wider text-stone-500">
              {t('account_logged_in_as')}
            </span>
            <p className="mt-0.5 truncate font-mono text-sm text-stone-100">
              {userEmail ?? '—'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => void onSignOut()}
            className="shrink-0 rounded-lg border border-red-900/80 bg-red-950/40 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-red-300 transition hover:bg-red-950/70"
          >
            {t('account_sign_out')}
          </button>
        </div>

        <div className="mt-3 border-t border-stone-800 pt-3">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-stone-400">
            <span className="shrink-0 text-stone-500">{t('account_status_label')}</span>
            {syncError ? (
              <span className="min-w-0 wrap-break-word text-red-400">
                {t('account_status_error_prefix')} {syncError}
              </span>
            ) : isSyncing ? (
              <span className="text-amber-400">{t('account_status_syncing')}</span>
            ) : (
              <span className="text-emerald-400">{t('account_status_ok')}</span>
            )}
            <span className="hidden text-stone-600 sm:inline" aria-hidden>
              ·
            </span>
            <span className="text-stone-500">
              {t('account_last_sync')}{' '}
              <span className="text-stone-400">
                {lastSyncAt ? formatDateTime(lastSyncAt) : t('account_never_synced')}
              </span>
            </span>
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            <button
              type="button"
              onClick={() => void flushSync()}
              className="rounded-md border border-stone-600 bg-stone-800/80 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-stone-200 transition hover:border-amber-700 hover:text-amber-300"
            >
              {t('account_upload')}
            </button>
            <button
              type="button"
              onClick={() => void syncFromCloud()}
              className="rounded-md border border-stone-600 bg-stone-800/80 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-stone-200 transition hover:border-amber-700 hover:text-amber-300"
            >
              {t('account_pull_cloud')}
            </button>
          </div>
        </div>
      </Section>

      <Section title={t('account_settings_title')}>
        <label className="block text-[11px] uppercase tracking-wider text-stone-500">
          {t('account_language')}
          <select
            value={settings.language}
            onChange={(e) =>
              setPartial({ language: e.target.value as LanguageCode })
            }
            className="mt-1 w-full rounded-lg border border-stone-700 bg-stone-950 px-3 py-2 text-sm text-stone-100 outline-none ring-amber-500/30 focus:border-amber-500 focus:ring-2"
          >
            <option value="nl">{t('account_lang_nl')}</option>
            <option value="en">{t('account_lang_en')}</option>
          </select>
        </label>
        <p className="mt-2 text-[11px] text-stone-500">
          {t('account_language_note')}
        </p>

        <ToggleRow
          label={t('account_notifications')}
          description={t('account_notifications_desc')}
          checked={settings.notificationsEnabled}
          disabled={pushBusy}
          onChange={() => {
            void (async () => {
              const next = !settings.notificationsEnabled
              if (!next) {
                await unsubscribeAllPushNotifications()
                setPartial({ notificationsEnabled: false })
                return
              }
              setPushBusy(true)
              const ok = await subscribeToPushNotifications()
              setPushBusy(false)
              if (!ok) {
                useGameStore.setState((prev) => ({
                  toasts: [
                    ...prev.toasts,
                    {
                      id: `toast-push-${Date.now()}`,
                      survivorName: 'shelter',
                      message: {
                        nl: translateStatic('nl', 'account_notifications_blocked'),
                        en: translateStatic('en', 'account_notifications_blocked'),
                      },
                      delivered: [],
                    },
                  ],
                }))
                return
              }
              setPartial({ notificationsEnabled: true })
            })()
          }}
        />
        <ToggleRow
          label={t('account_sound')}
          description={t('account_sound_desc')}
          checked={settings.soundEnabled}
          onChange={() => {
            void (async () => {
              const next = !settings.soundEnabled
              if (next) await resumeAudioContext()
              setPartial({ soundEnabled: next })
            })()
          }}
        />
        <ToggleRow
          label={t('account_theme')}
          description={t('account_theme_desc')}
          checked={settings.theme === 'light'}
          onChange={() =>
            setPartial({ theme: settings.theme === 'dark' ? 'light' : 'dark' })
          }
        />
      </Section>

      <Section title={t('account_about_title')}>
        <p className="text-sm text-stone-400">{t('account_about_body')}</p>
      </Section>

      <Section title={t('account_install_app_title')}>
        {canInstall ? (
          <>
            <p className="text-sm text-stone-400">{t('account_install_app_desc')}</p>
            <button
              type="button"
              onClick={() => void promptPWAInstall()}
              className="mt-3 w-full rounded-lg border border-amber-700/70 bg-amber-950/30 px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-amber-200 transition hover:bg-amber-950/60"
            >
              {t('account_install_app_button')}
            </button>
          </>
        ) : (
          <p className="text-sm text-stone-500">{t('account_install_app_unavailable')}</p>
        )}
      </Section>

      <Section title={t('account_reset_section')}>
        <p className="text-sm text-stone-400">{t('account_reset_hint')}</p>
        <button
          type="button"
          onClick={() => {
            if (resetConfirmStep === 0) {
              setResetConfirmStep(1)
              return
            }
            resetProgressToNewGame()
            setResetConfirmStep(0)
          }}
          className="mt-3 w-full rounded-lg border border-red-900/70 bg-red-950/35 px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-red-200 transition hover:bg-red-950/60"
        >
          {resetConfirmStep === 0
            ? t('account_reset_first')
            : t('account_reset_confirm_prompt')}
        </button>
        {resetConfirmStep === 1 && (
          <button
            type="button"
            onClick={() => setResetConfirmStep(0)}
            className="mt-2 text-[11px] text-stone-500 underline decoration-stone-600 underline-offset-2 hover:text-stone-300"
          >
            {t('account_reset_cancel')}
          </button>
        )}
      </Section>

    
    </div>
  )
}

function Section({
  title,
  children,
}: {
  title: string
  children: ReactNode
}) {
  return (
    <section>
      <h2 className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-stone-500">
        {title}
      </h2>
      <div className="rounded-xl border border-stone-800 bg-stone-800/60 p-4">
        {children}
      </div>
    </section>
  )
}

function ToggleRow({
  label,
  description,
  checked,
  disabled,
  onChange,
}: {
  label: string
  description: string
  checked: boolean
  disabled?: boolean
  onChange: () => void
}) {
  return (
    <label
      className={`mt-4 flex cursor-pointer items-start gap-3 rounded-lg border border-stone-800/80 p-3 ${disabled ? 'opacity-60' : ''}`}
    >
      <input
        type="checkbox"
        className="mt-1 accent-amber-500"
        checked={checked}
        disabled={disabled}
        onChange={() => onChange()}
      />
      <span>
        <span className="block text-sm font-medium text-stone-200">
          {label}
        </span>
        <span className="block text-[11px] text-stone-500">{description}</span>
      </span>
    </label>
  )
}
